/**
 * Fullscreen Icon Component
 * Reusable SVG icon for fullscreen toggle
 */

import React from 'react';

export interface FullscreenIconProps {
  /**
   * Icon color (default: #666)
   */
  color?: string;

  /**
   * Icon size in pixels (default: 16)
   */
  size?: number;

  /**
   * CSS class for styling
   */
  className?: string;

  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;

  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent) => void;

  /**
   * Whether fullscreen is active (true shows exit icon, false shows enter icon)
   */
  isFullscreen?: boolean;
}

/**
 * Fullscreen Icon - SVG expand/collapse icon
 * Shows expand arrows when not fullscreen, collapse arrows when fullscreen
 */
export const FullscreenIcon: React.FC<FullscreenIconProps> = ({
  color = '#666',
  size = 16,
  className = '',
  ariaLabel = 'Fullscreen',
  onClick,
  isFullscreen = false,
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`fullscreen-icon ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'color 200ms ease',
        flexShrink: 0,
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          onClick(e as any);
        }
      }}
    >
      {isFullscreen ? (
        <>
          {/* Exit fullscreen icon - collapse arrows */}
          {/* Top-left collapse */}
          <polyline points="8 3 3 3 3 8" />
          <line x1="3" y1="3" x2="8" y2="8" />
          {/* Top-right collapse */}
          <polyline points="16 3 21 3 21 8" />
          <line x1="21" y1="3" x2="16" y2="8" />
          {/* Bottom-left collapse */}
          <polyline points="8 21 3 21 3 16" />
          <line x1="3" y1="21" x2="8" y2="16" />
          {/* Bottom-right collapse */}
          <polyline points="16 21 21 21 21 16" />
          <line x1="21" y1="21" x2="16" y2="16" />
        </>
      ) : (
        <>
          {/* Enter fullscreen icon - expand arrows */}
          {/* Top-left expand */}
          <polyline points="3 8 3 3 8 3" />
          <line x1="3" y1="3" x2="8" y2="8" />
          {/* Top-right expand */}
          <polyline points="21 8 21 3 16 3" />
          <line x1="21" y1="3" x2="16" y2="8" />
          {/* Bottom-left expand */}
          <polyline points="3 16 3 21 8 21" />
          <line x1="3" y1="21" x2="8" y2="16" />
          {/* Bottom-right expand */}
          <polyline points="21 16 21 21 16 21" />
          <line x1="21" y1="21" x2="16" y2="16" />
        </>
      )}
    </svg>
  );
};

export default FullscreenIcon;
